package com.evoapps.evoshift.ui.roster

import androidx.compose.runtime.getValue
import androidx.compose.animation.animateColorAsState
import com.evoapps.evoshift.ui.theme.motionTween
import com.evoapps.evoshift.ui.theme.animatedFraction
import com.evoapps.evoshift.ui.theme.AnimatedAmountText
import com.evoapps.evoshift.domain.explain.Calculation
import com.evoapps.evoshift.ui.finance.ExplainableAmount

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Warning
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.budget.BudgetComparison
import com.evoapps.evoshift.domain.budget.BudgetEffect
import com.evoapps.evoshift.domain.budget.BudgetUse
import com.evoapps.evoshift.domain.budget.LabourBudgetStatus
import com.evoapps.evoshift.domain.budget.OpenShiftsCost
import com.evoapps.evoshift.ui.util.formatHintHours
import evoshift.composeapp.generated.resources.labour_budget_open_estimated
import evoshift.composeapp.generated.resources.labour_budget_open_unpriced
import com.evoapps.evoshift.domain.budget.WeekBudgetUse
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.finance.formatMoney
import com.evoapps.evoshift.widgets.ErrorOnNavy
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.labour_budget
import evoshift.composeapp.generated.resources.labour_budget_actual
import evoshift.composeapp.generated.resources.labour_budget_amount
import evoshift.composeapp.generated.resources.labour_budget_effect_left
import evoshift.composeapp.generated.resources.labour_budget_effect_over
import evoshift.composeapp.generated.resources.labour_budget_effect_warning
import evoshift.composeapp.generated.resources.labour_budget_effect_week_left
import evoshift.composeapp.generated.resources.labour_budget_effect_week_over
import evoshift.composeapp.generated.resources.labour_budget_left
import evoshift.composeapp.generated.resources.labour_budget_over
import evoshift.composeapp.generated.resources.labour_budget_planned
import evoshift.composeapp.generated.resources.labour_budget_used
import evoshift.composeapp.generated.resources.labour_budget_week
import org.jetbrains.compose.resources.stringResource

/*
 * SH-115: the labour budget on screen: the roster builder's header bar (and its weeks), a shift
 * change's effect in the shift dialogs, and the Money tab's budget against plan and actual.
 * Money roles only: the view models give a budget to nobody else.
 */

private val OverRed = Color(0xFFD32F2F)
private val WithinGreen = Color(0xFF2E7D32)

/** "+240,00 zł" / "-240,00 zł": a change's cost with its sign. */
fun formatMoneyDelta(amount: Double, currency: String): String =
    if (amount >= 0) "+${formatMoney(amount, currency)}" else formatMoney(amount, currency)

/** "1 150,00 zł left" or "400,00 zł over budget". */
@Composable
fun budgetRemainingText(use: BudgetUse, currency: String): String =
    if (use.isOver) stringResource(Res.string.labour_budget_over, formatMoney(-use.remaining, currency))
    else stringResource(Res.string.labour_budget_left, formatMoney(use.remaining, currency))

/** A thin bar: how much of the budget is used, red once over. */
@Composable
private fun BudgetBar(use: BudgetUse, modifier: Modifier = Modifier, track: Color = DeepNavy.copy(alpha = 0.1f)) {
    // SH-158: a change to the plan moves the bar and shifts its colour instead of jumping.
    val fill = animatedFraction(use.share.coerceIn(0.0, 1.0).toFloat())
    val color by animateColorAsState(
        if (use.isOver) OverRed else if (use.share > 0.9) DarkGold else WithinGreen,
        motionTween(),
        label = "budgetBar"
    )
    Box(modifier.height(8.dp).clip(RoundedCornerShape(4.dp)).background(track)) {
        if (fill > 0f) {
            Box(Modifier.fillMaxWidth(fill).fillMaxHeight().background(color))
        }
    }
}

/**
 * The builder's header line: "Labour budget  8 400,00 zł of 10 000,00 zł · 1 600,00 zł left" with
 * a bar, and each week's use when a weekly budget is set (over in red).
 */
@OptIn(ExperimentalLayoutApi::class)
@Composable
fun LabourBudgetHeader(
    status: LabourBudgetStatus,
    currency: String,
    modifier: Modifier = Modifier,
    /** SH-152: what the used amount is made of, worked out when tapped. */
    usedWhy: (() -> Calculation?)? = null
) {
    val over = status.month.isOver
    Column(modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(4.dp)) {
        FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(2.dp)) {
            Text(stringResource(Res.string.labour_budget), style = MaterialTheme.typography.labelMedium, color = DeepNavy.copy(alpha = 0.7f))
            ExplainableAmount(
                stringResource(Res.string.labour_budget_used, formatMoney(status.month.used, currency), formatMoney(status.month.budget, currency)),
                explain = usedWhy,
                currency = currency,
                style = MaterialTheme.typography.labelMedium,
                fontWeight = FontWeight.Bold,
                color = DeepNavy
            )
            Row(verticalAlignment = Alignment.CenterVertically) {
                if (over) Icon(Icons.Default.Warning, contentDescription = null, tint = OverRed, modifier = Modifier.size(14.dp).padding(end = 2.dp))
                AnimatedAmountText(
                    budgetRemainingText(status.month, currency),
                    style = MaterialTheme.typography.labelMedium,
                    fontWeight = FontWeight.Bold,
                    color = if (over) OverRed else WithinGreen
                )
            }
        }
        BudgetBar(status.month, Modifier.fillMaxWidth())
        // Unassigned shifts are in the planned figure only as an estimate, or not at all: say which.
        if (status.open.shifts > 0) {
            Text(
                openShiftsText(status.open, currency),
                style = MaterialTheme.typography.labelSmall,
                color = DeepNavy.copy(alpha = 0.7f)
            )
        }
        if (status.weeks.isNotEmpty()) {
            FlowRow(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalArrangement = Arrangement.spacedBy(2.dp)) {
                status.weeks.forEach { WeekBudgetChip(it, currency) }
            }
        }
    }
}

/** "Unassigned shifts: 3 (22 h), estimated at 880,00 zł", or that they are not counted. */
@Composable
fun openShiftsText(open: OpenShiftsCost, currency: String): String {
    val hours = formatHintHours(open.hours)
    val estimate = open.estimate
    return if (estimate != null) stringResource(Res.string.labour_budget_open_estimated, open.shifts, hours, formatMoney(estimate, currency))
    else stringResource(Res.string.labour_budget_open_unpriced, open.shifts, hours)
}

@Composable
private fun WeekBudgetChip(week: WeekBudgetUse, currency: String) {
    val text = stringResource(
        Res.string.labour_budget_week,
        week.start.day, week.end.day, formatMoney(week.use.used, currency), formatMoney(week.use.budget, currency)
    )
    Text(
        text,
        style = MaterialTheme.typography.labelSmall,
        fontWeight = if (week.use.isOver) FontWeight.Bold else FontWeight.Normal,
        color = if (week.use.isOver) OverRed else DeepNavy.copy(alpha = 0.7f)
    )
}

/**
 * A shift change's effect in the (dark) shift dialogs: "+240,00 zł, 1 150,00 zł left", the week's
 * remaining when a weekly budget is set, and a warning once over. It never stops the save.
 */
@Composable
fun BudgetEffectLines(effect: BudgetEffect, currency: String, modifier: Modifier = Modifier) {
    val delta = formatMoneyDelta(effect.delta, currency)
    val month = if (effect.month.isOver) {
        stringResource(Res.string.labour_budget_effect_over, delta, formatMoney(-effect.month.remaining, currency))
    } else {
        stringResource(Res.string.labour_budget_effect_left, delta, formatMoney(effect.month.remaining, currency))
    }
    Column(modifier, verticalArrangement = Arrangement.spacedBy(2.dp)) {
        Text(
            month,
            style = MaterialTheme.typography.bodyMedium,
            fontWeight = FontWeight.Bold,
            color = if (effect.month.isOver) ErrorOnNavy else DarkGold
        )
        effect.week?.let { week ->
            Text(
                if (week.use.isOver) stringResource(Res.string.labour_budget_effect_week_over, formatMoney(-week.use.remaining, currency))
                else stringResource(Res.string.labour_budget_effect_week_left, formatMoney(week.use.remaining, currency)),
                style = MaterialTheme.typography.bodySmall,
                color = if (week.use.isOver) ErrorOnNavy else Color.White.copy(alpha = 0.75f)
            )
        }
        if (effect.isOver) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                Icon(Icons.Default.Warning, contentDescription = null, tint = ErrorOnNavy, modifier = Modifier.size(16.dp))
                Text(stringResource(Res.string.labour_budget_effect_warning), style = MaterialTheme.typography.bodySmall, color = ErrorOnNavy)
            }
        }
    }
}

/** The Money tab's card: the month's budget against the planned and the actual labour cost. */
@Composable
fun BudgetComparisonCard(comparison: BudgetComparison, currency: String, modifier: Modifier = Modifier) {
    Column(
        modifier.fillMaxWidth().clip(RoundedCornerShape(16.dp)).background(Color.White).padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(stringResource(Res.string.labour_budget), style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold, color = DeepNavy, modifier = Modifier.weight(1f))
            Text(
                stringResource(Res.string.labour_budget_amount) + " " + formatMoney(comparison.budget, currency),
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = DeepNavy
            )
        }
        BudgetRow(stringResource(Res.string.labour_budget_planned), comparison.planned, currency)
        comparison.actual?.let { BudgetRow(stringResource(Res.string.labour_budget_actual), it, currency) }
    }
}

@Composable
private fun BudgetRow(label: String, use: BudgetUse, currency: String) {
    val remaining = budgetRemainingText(use, currency)
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Text(label, style = MaterialTheme.typography.bodyMedium, color = DeepNavy.copy(alpha = 0.7f), modifier = Modifier.weight(1f))
            Text(formatMoney(use.used, currency), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = DeepNavy)
        }
        BudgetBar(use, Modifier.fillMaxWidth())
        Text(remaining, style = MaterialTheme.typography.bodySmall, fontWeight = FontWeight.Bold, color = if (use.isOver) OverRed else WithinGreen)
    }
}
